import Tabs from "../tabs";
import { Card, CardContent, Grid, Box, Button, Stack, CircularProgress, Typography } from "@mui/material";
import { FieldSelect } from "../../../../components/selections/fieldInputs";
import FilterMultiSelect from "../../../../components/selections/filterMultiSelect";
import { useState, useEffect } from "react";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import { addBranchDepartmentThunk, branchDepartmentThunk } from "../../../../components/general/generalThunk";
import { APP_ROUTES } from "../../../../config/appRoutes";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { stepCardSx, stepActionsSx, nextBtnSx, backBtnSx, StepHeader } from "../../../campaign/components/stepUi";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AddLinkOutlinedIcon from "@mui/icons-material/AddLinkOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Create-branch-department form. `embedded` = inside the list page popup (no page header, Cancel button,
// onSuccess(message) closes the popup and the list shows the message).
export function BranchDepartmentCreateForm({ embedded = false, onCancel, onSuccess }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { branches, departments, branchDepartments, status, error } = useSelector((state) => state.general);

    const [state, setState] = useState({
        departmentValue: "",
        selectedBranchValue: [], // nothing selected by default
        filteredBranches: [], // options for MultiSelect
        errors: {},
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Handle department selection
    const handleSingleSelectChange = (name, selectedValue) => {
        // Get branch ids already used in this department
        const usedBranchIds = branchDepartments
            .filter((item) => +item.department_id === +selectedValue)
            .map((item) => item.branch_id);

        // Prepare branch options excluding already used
        const remainingBranches = branches
            .filter((branch) => !usedBranchIds.includes(branch.id))
            .map((branch) => ({ label: branch.branch_name, value: branch.id }));

        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            selectedBranchValue: [], // reset MultiSelect selection
            filteredBranches: remainingBranches,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    // Handle MultiSelect branch selection
    const handleMultiSelectChange = (name, selectedValues) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValues,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        const validations = [
            { valid: !!state.departmentValue, message: "Please Select Department", field: "departmentValue" },
            { valid: state.selectedBranchValue.length > 0, message: "Please Select Branch", field: "selectedBranchValue" },
        ];

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar({ open: true, message: rule.message, severity: "error" });
                setState((prev) => ({ ...prev, errors: { ...prev.errors, [rule.field]: rule.message } }));
                return;
            }
        }

        try {
            const result = await dispatch(addBranchDepartmentThunk({
                credentials: {
                    branch_ids: state.selectedBranchValue.map((item) => item.value).toString(),
                    department_id: state.departmentValue,
                }
            }));

            if (result.meta.requestStatus === "fulfilled") {
                if (onSuccess) {
                    dispatch(branchDepartmentThunk({ type: "getBranchDepartment" }));
                    onSuccess(result.payload?.message);
                    return;
                }
                setSnackbar({ open: true, message: result.payload.message, severity: "success" });
                dispatch(branchDepartmentThunk({ type: "getBranchDepartment" }));
                navigate(APP_ROUTES.BRANCH_DEPARTMENT.LIST);
            } else {
                setSnackbar({ open: true, message: result.payload, severity: "error" });
            }
        } catch {
            setSnackbar({ open: true, message: "Server error. Please try again.", severity: "error" });
        }
    };

    // Display API error in snackbar
    useEffect(() => {
        if (error) {
            setSnackbar({ open: true, message: error, severity: "error" });
        }
    }, [error]);

    const Wrap = embedded ? Box : Tabs;

    return (
        <div>
            <Wrap>
                <Box mt={embedded ? 0 : 1}>
                    <Card elevation={0} sx={embedded ? { borderRadius: 0, boxShadow: "none", overflow: "visible" } : stepCardSx}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            {!embedded && (
                                <StepHeader
                                    icon={<AddLinkOutlinedIcon sx={{ fontSize: 20 }} />}
                                    title="Create Branch Department"
                                    subtitle="Link a department to one or more branches"
                                />
                            )}
                            <Grid container spacing={2}>
                                <Grid item size={embedded ? { xs: 12, sm: 6 } : { xs: 12, sm: 6, md: 4 }}>
                                    <FieldSelect
                                        name="departmentValue"
                                        label="Department"
                                        options={departments.map((item) => ({ value: item.id, label: item.department_name }))}
                                        value={state.departmentValue}
                                        onChange={handleSingleSelectChange}
                                        error={!!state.errors?.departmentValue}
                                        icon={<BusinessOutlinedIcon />}
                                        placeholder="Select department"
                                        required
                                    />
                                </Grid>

                                <Grid item size={embedded ? { xs: 12, sm: 6 } : { xs: 12, sm: 6, md: 4 }}>
                                    <FilterMultiSelect
                                        name="selectedBranchValue"
                                        label="Select Branch"
                                        options={state.filteredBranches}
                                        value={state.selectedBranchValue}
                                        onChange={handleMultiSelectChange}
                                        error={!!state.errors?.selectedBranchValue}
                                        formHelperText={false}
                                        disabled={!state.departmentValue} // disabled until department selected
                                    />
                                </Grid>
                            </Grid>

                            {/* hint */}
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5, color: "#64748b" }}>
                                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                                <Typography sx={{ fontSize: 12 }}>
                                    {state.departmentValue
                                        ? "Only branches not yet linked to this department are listed."
                                        : "Choose a department first, then pick its branches."}
                                </Typography>
                            </Stack>
                        </CardContent>

                        {/* Footer actions */}
                        <Box sx={stepActionsSx}>
                            <Stack direction="row" spacing={1.5}>
                                {embedded && (
                                    <Button onClick={onCancel} sx={backBtnSx}>Cancel</Button>
                                )}
                                <Button
                                    disabled={status === "loading"}
                                    onClick={status === "loading" ? null : handleSubmit}
                                    startIcon={status === "loading" ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AddCircleOutlineIcon />}
                                    sx={nextBtnSx}
                                >
                                    {status === "loading" ? "Submitting.." : "Create Branch Dept"}
                                </Button>
                            </Stack>
                        </Box>
                    </Card>
                </Box>
            </Wrap>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
}

// /branch-department-creation page
export default function BranchDepartmentCreation() {
    return <BranchDepartmentCreateForm />;
}
